<template>
  <v-dialog v-model="internalDialogVisible" max-width="75%">
    <v-card>
      <v-card-title>
        <span class="headline">Change Password</span>
      </v-card-title>

      <v-card-text>
        <v-form
            ref="form"
            lazy-validation>
            <v-container>
              <v-row>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field
                    v-model="Password"
                    type="password"
                    label="Password"
                    :rules="requiredField"
                  ></v-text-field>
                  
                  <!-- Password strength indicator -->
                  <div v-if="Password" class="mt-2">
                    <v-progress-linear
                      :value="passwordStrengthScore * 20"
                      :color="passwordStrengthColor"
                      height="8"
                      rounded
                    ></v-progress-linear>
                    <div class="text-caption mt-1" :class="`${passwordStrengthColor}--text`">
                      {{ passwordStrengthMessage }}
                    </div>
                  </div>
                  
                  <!-- Password requirements -->
                  <v-card v-if="Password" class="mt-2 pa-2" outlined>
                    <div class="text-caption font-weight-bold mb-1">Password Requirements:</div>
                    <div class="text-caption" :class="passwordRequirements.length ? 'green--text' : 'red--text'">
                      ✓ At least 8 characters
                    </div>
                    <div class="text-caption" :class="passwordRequirements.uppercase ? 'green--text' : 'red--text'">
                      ✓ At least one uppercase letter (A-Z)
                    </div>
                    <div class="text-caption" :class="passwordRequirements.lowercase ? 'green--text' : 'red--text'">
                      ✓ At least one lowercase letter (a-z)
                    </div>
                    <div class="text-caption" :class="passwordRequirements.numbers ? 'green--text' : 'red--text'">
                      ✓ At least one number (0-9)
                    </div>
                    <div class="text-caption" :class="passwordRequirements.special ? 'green--text' : 'red--text'">
                      ✓ At least one special character (!@#$%^&*)
                    </div>
                  </v-card>
                </v-col>

                  <v-col cols="12" sm="12" md="12">
                    <v-text-field
                      v-model="RePassword"
                      type="password"
                      label="Retype Password"
                      :rules="requiredField"
                    ></v-text-field>
                  </v-col>
                  
                  <!-- Password validation message -->
                  <v-col cols="12" v-if="passwordMatchMessage">
                    <v-alert
                      type="error"
                      dense
                      dismissible
                    >
                      {{ passwordMatchMessage }}
                    </v-alert>
                  </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
            <v-btn color="blue darken-1" text @click="updatePassword()">Update</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
        </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import axios from 'axios'
import Swal from 'sweetalert2'

const url = require("../../../client_config/config").url;

export default {
  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    employeeID:{
      type: String,
      required: true
    }
  },
  data: function()  {
    return {
      requiredField: [
          v => !!v || 'This is required',
      ],
      internalDialogVisible: this.dialogVisible,
      Password: "",
      RePassword: "",
    }
  },
  computed: {
    // Password validation message
    passwordMatchMessage() {
      if (this.Password && this.RePassword) {
        return this.Password === this.RePassword ? '' : 'Passwords do not match';
      }
      return '';
    },

    // Password strength properties
    passwordRequirements() {
      return this.Password ? this.validatePasswordStrength(this.Password) : {
        length: false,
        uppercase: false,
        lowercase: false,
        numbers: false,
        special: false
      };
    },

    passwordStrengthMessage() {
      return this.Password ? this.getPasswordStrengthMessage(this.Password) : '';
    },

    passwordStrengthColor() {
      return this.Password ? this.getPasswordStrengthColor(this.Password) : 'grey';
    },

    passwordStrengthScore() {
      return this.Password ? this.getPasswordStrengthScore(this.Password) : 0;
    },
  },
  watch: {
    dialogVisible(newVal) {
      this.internalDialogVisible = newVal;
    },
    internalDialogVisible(newVal) {
      if (!newVal) {
        this.$emit('closeDialog');
      }
    },
    employeeID(newVal) {
      this.employeeID = newVal;
      this.Password = "";
      this.RePassword = "";
      if(this.$refs.form){
        this.$refs.form.reset()
      }
    }
  },
  methods: {

    // Password comparison function
    checkPasswordMatch() {
      if (this.Password && this.RePassword) {
        return this.Password === this.RePassword;
      }
      return false;
    },

    // Password strength validation
    validatePasswordStrength(password) {
      const requirements = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        numbers: /[0-9]/.test(password),
        special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
      };
      
      return requirements;
    },

    // Get password strength score (0-5)
    getPasswordStrengthScore(password) {
      if (!password) return 0;
      
      const requirements = this.validatePasswordStrength(password);
      return Object.values(requirements).filter(Boolean).length;
    },

    // Get password strength message
    getPasswordStrengthMessage(password) {
      if (!password) return '';
      
      const score = this.getPasswordStrengthScore(password);
      
      if (score === 5) return 'Strong password';
      if (score === 4) return 'Good password';
      if (score === 3) return 'Fair password';
      if (score === 2) return 'Weak password';
      if (score === 1) return 'Very weak password';
      return 'Invalid password';
    },

    // Get password strength color
    getPasswordStrengthColor(password) {
      const score = this.getPasswordStrengthScore(password);
      
      if (score === 5) return 'success';
      if (score === 4) return 'info';
      if (score === 3) return 'warning';
      if (score <= 2) return 'error';
      return 'grey';
    },

    validate() {
      return this.$refs.form.validate()
    },

    close() {
      this.internalDialogVisible = false;
    },

    updatePassword() {
      Swal.fire({
        title: 'Please wait...',
        text: 'Updating password...',
        icon: 'info',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });
      if (this.validate()) {
        if (this.checkPasswordMatch()) {
          let req_body = {
              password: this.Password
          };

          axios.put(`${url}/users/${this.employeeID}/update-password`, req_body).then((response) => {
              if (response.status === 400) {
                  response.json().then(function (data) {
                      this.msg = true;
                      this.text = data.msg;
                      Swal.fire({
                        icon: 'danger',
                        title: 'Danger',
                        text: "Error encountered upon updating password.",
                      })
                  });
              }

              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: "The user has been updated successfully.",
              }).then((result) => {
                if (result.value) {
                  this.close();
                }
              });

          });

        } else {
          this.text = "The passwords do not match, please try again.";
          this.msg = true;
        }
      }
    },
  },
}
</script>
